import { create } from 'zustand'
import { ParsedBrand, FilterState, KPIData, AmazonPresence, ManufacturingVerification, PriceTier } from '../types/brand'
import { BrandCreateInput, BrandUpdateInput } from '../services/brandService'
import { getBrandService } from '../services/brandServiceFactory'
import { calculateKPIs } from '../utils/csvLoader'
import { 
  updateURLWithFilters, 
  decodeFiltersFromURL, 
  validateFiltersFromURL,
  hasURLFilters 
} from '../utils/urlState'

interface BrandOperationResult {
  success: boolean
  error?: string
  data?: ParsedBrand | ParsedBrand[]
}

interface EnhancedBrandStore {
  // Data
  allBrands: ParsedBrand[]
  filteredBrands: ParsedBrand[]
  isLoading: boolean
  error: string | null
  lastRefresh: Date | null
  
  // Filters
  filters: FilterState
  
  // KPIs
  kpis: KPIData | null
  
  // Filter options (derived from data)
  availableProductCategories: string[]
  availableStates: string[]
  
  // Cached range calculations to prevent recalculation on every render
  cachedFoundedYearRange: [number, number]
  cachedConfidenceScoreRange: [number, number]
  
  // CRUD operation states
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  operationError: string | null
  
  // Optimistic updates tracking
  pendingChanges: ParsedBrand[]
  
  // Actions - Original
  loadData: () => Promise<void>
  refreshData: () => Promise<void>
  setSearchTerm: (term: string) => void
  setAmazonPresenceFilter: (values: AmazonPresence[]) => void
  setManufacturingVerificationFilter: (values: ManufacturingVerification[]) => void
  setPriceTierFilter: (values: PriceTier[]) => void
  setProductCategoriesFilter: (values: string[]) => void
  setFoundedYearRange: (range: [number, number]) => void
  setConfidenceScoreRange: (range: [number, number]) => void
  setStatesFilter: (values: string[]) => void
  setIncludeUnknownFoundedYear: (include: boolean) => void
  clearAllFilters: () => void
  applyFilters: () => void
  loadFiltersFromURL: () => void
  updateURL: () => void
  
  // Actions - CRUD Operations
  createBrand: (brand: BrandCreateInput) => Promise<BrandOperationResult>
  updateBrand: (brandName: string, updates: BrandUpdateInput) => Promise<BrandOperationResult>
  deleteBrand: (brandName: string) => Promise<BrandOperationResult>
  bulkCreateBrands: (brands: BrandCreateInput[]) => Promise<BrandOperationResult>
  bulkUpdateBrands: (updates: { brandName: string; updates: BrandUpdateInput }[]) => Promise<BrandOperationResult>
  bulkDeleteBrands: (brandNames: string[]) => Promise<BrandOperationResult>
  
  // Actions - Utility
  getBrandByName: (brandName: string) => ParsedBrand | null
  searchBrands: (query: string) => ParsedBrand[]
  clearOperationError: () => void
  revertOptimisticUpdate: (brandName: string) => void
  commitOptimisticUpdates: () => void
}

const initialFilters: FilterState = {
  searchTerm: '',
  amazonPresence: [],
  manufacturingVerification: [],
  priceTier: [],
  productCategories: [],
  foundedYearRange: [1801, 2025],
  confidenceScoreRange: [0, 10],
  states: [],
  includeUnknownFoundedYear: true  // Default to true to maintain existing behavior
}

function filterBrands(brands: ParsedBrand[], filters: FilterState): ParsedBrand[] {
  return brands.filter(brand => {
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      const matchesSearch = 
        brand.brand.toLowerCase().includes(searchLower) ||
        brand.consumer_summary.toLowerCase().includes(searchLower) ||
        brand.primary_products.toLowerCase().includes(searchLower)
      
      if (!matchesSearch) return false
    }
    
    // Amazon presence filter
    if (filters.amazonPresence.length > 0 && !filters.amazonPresence.includes(brand.amazon_presence)) {
      return false
    }
    
    // Manufacturing verification filter
    if (filters.manufacturingVerification.length > 0 && !filters.manufacturingVerification.includes(brand.manufacturing_verification)) {
      return false
    }
    
    // Price tier filter
    if (filters.priceTier.length > 0 && !filters.priceTier.includes(brand.price_tier)) {
      return false
    }
    
    // Product categories filter (use clean categories)
    if (filters.productCategories.length > 0) {
      const hasMatchingCategory = filters.productCategories.some(category =>
        brand.clean_categories.includes(category)
      )
      if (!hasMatchingCategory) return false
    }
    
    // Founded year range filter
    if (typeof brand.founded_year === 'number') {
      if (brand.founded_year < filters.foundedYearRange[0] || brand.founded_year > filters.foundedYearRange[1]) {
        return false
      }
    } else if (brand.founded_year === 'unknown') {
      // Include unknown founded years only if the option is enabled
      if (!filters.includeUnknownFoundedYear) {
        return false
      }
    }
    
    // Confidence score range filter
    if (brand.confidence_score < filters.confidenceScoreRange[0] || brand.confidence_score > filters.confidenceScoreRange[1]) {
      return false
    }
    
    // States filter - exclude brands with unknown states when state filters are active
    if (filters.states.length > 0) {
      if (!brand.hq_state || !filters.states.includes(brand.hq_state)) return false
    }
    
    return true
  })
}

// Debounced URL update to prevent page jumping
let urlUpdateTimeout: NodeJS.Timeout | null = null
const debouncedUpdateURL = () => {
  if (urlUpdateTimeout) {
    clearTimeout(urlUpdateTimeout)
  }
  urlUpdateTimeout = setTimeout(() => {
    // Preserve scroll position to prevent jumping
    const scrollY = window.scrollY
    const scrollX = window.scrollX
    
    const filters = useEnhancedBrandStore.getState().filters
    updateURLWithFilters(filters)
    
    // Restore scroll position after URL update
    requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY)
    })
  }, 500) // Increased debounce to 500ms for better UX during slider dragging
}

export const useEnhancedBrandStore = create<EnhancedBrandStore>((set, get) => ({
  // Initial state
  allBrands: [],
  filteredBrands: [],
  isLoading: false,
  error: null,
  lastRefresh: null,
  filters: initialFilters,
  kpis: null,
  availableProductCategories: [],
  availableStates: [],
  
  // Cached ranges
  cachedFoundedYearRange: [1801, 2025],
  cachedConfidenceScoreRange: [0, 10],
  
  // CRUD states
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  operationError: null,
  pendingChanges: [],
  
  // Original Actions
  loadData: async () => {
    set({ isLoading: true, error: null })
    
    try {
      const brandService = getBrandService()
      const result = await brandService.getAllBrands()
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to load brands')
      }
      
      const brands = result.data
      
      // Extract unique values for filter options (use clean categories)
      const productCategories = Array.from(
        new Set(brands.flatMap(b => b.clean_categories))
      ).sort()
      
      const states = Array.from(
        new Set(brands.map(b => b.hq_state).filter(Boolean))
      ).sort() as string[]
      
      // Calculate actual year range from data
      const foundedYears = brands
        .map(b => b.founded_year)
        .filter((year): year is number => typeof year === 'number')
      
      const actualMinYear = foundedYears.length > 0 ? Math.min(...foundedYears) : 1801
      const actualMaxYear = foundedYears.length > 0 ? Math.max(...foundedYears) : 2025
      
      // Calculate actual confidence score range from data
      const confidenceScores = brands
        .map(b => b.confidence_score)
        .filter((score): score is number => typeof score === 'number' && !isNaN(score))
      
      const actualMinScore = confidenceScores.length > 0 ? Math.min(...confidenceScores) : 0
      const actualMaxScore = confidenceScores.length > 0 ? Math.max(...confidenceScores) : 10
      
      // Update filters with actual data ranges
      const updatedFilters = {
        ...get().filters,
        foundedYearRange: [actualMinYear, actualMaxYear] as [number, number],
        confidenceScoreRange: [actualMinScore, actualMaxScore] as [number, number]
      }
      
      set({
        allBrands: brands,
        availableProductCategories: productCategories,
        availableStates: states,
        filters: updatedFilters,
        cachedFoundedYearRange: [actualMinYear, actualMaxYear],
        cachedConfidenceScoreRange: [actualMinScore, actualMaxScore],
        lastRefresh: new Date(),
        isLoading: false
      })
      
      // Apply filters consistently to set filteredBrands and calculate KPIs
      get().applyFilters()
      
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load data',
        isLoading: false
      })
    }
  },

  refreshData: async () => {
    const currentFilters = get().filters
    
    set({ isLoading: true, error: null })
    
    try {
      const brandService = getBrandService()
      const result = await brandService.refreshData()
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to refresh data')
      }
      
      // Reload all data
      await get().loadData()
      
      // Restore current filters
      set({ filters: currentFilters })
      get().applyFilters()
      
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to refresh data',
        isLoading: false
      })
    }
  },
  
  setSearchTerm: (term: string) => {
    set(state => ({
      filters: { ...state.filters, searchTerm: term }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  setAmazonPresenceFilter: (values: AmazonPresence[]) => {
    set(state => ({
      filters: { ...state.filters, amazonPresence: values }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  setManufacturingVerificationFilter: (values: ManufacturingVerification[]) => {
    set(state => ({
      filters: { ...state.filters, manufacturingVerification: values }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  setPriceTierFilter: (values: PriceTier[]) => {
    set(state => ({
      filters: { ...state.filters, priceTier: values }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  setProductCategoriesFilter: (values: string[]) => {
    set(state => ({
      filters: { ...state.filters, productCategories: values }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  setFoundedYearRange: (range: [number, number]) => {
    set(state => ({
      filters: { ...state.filters, foundedYearRange: range }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  setConfidenceScoreRange: (range: [number, number]) => {
    set(state => ({
      filters: { ...state.filters, confidenceScoreRange: range }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  setStatesFilter: (values: string[]) => {
    set(state => ({
      filters: { ...state.filters, states: values }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  setIncludeUnknownFoundedYear: (include: boolean) => {
    set(state => ({
      filters: { ...state.filters, includeUnknownFoundedYear: include }
    }))
    get().applyFilters()
    debouncedUpdateURL()
  },
  
  clearAllFilters: () => {
    const { cachedFoundedYearRange, cachedConfidenceScoreRange } = get()
    
    // Use cached ranges instead of recalculating
    const resetFilters = {
      ...initialFilters,
      foundedYearRange: cachedFoundedYearRange,
      confidenceScoreRange: cachedConfidenceScoreRange
    }
    
    set({ filters: resetFilters })
    get().applyFilters()
    // Immediate URL update for clear filters
    get().updateURL()
  },
  
  applyFilters: () => {
    const { allBrands, filters, pendingChanges } = get()
    
    // Merge pending changes with all brands for filtering
    const brandsWithPendingChanges = [...allBrands]
    pendingChanges.forEach(pendingBrand => {
      const existingIndex = brandsWithPendingChanges.findIndex(b => b.brand === pendingBrand.brand)
      if (existingIndex >= 0) {
        brandsWithPendingChanges[existingIndex] = pendingBrand
      } else {
        brandsWithPendingChanges.push(pendingBrand)
      }
    })
    
    const filtered = filterBrands(brandsWithPendingChanges, filters)
    const kpis = calculateKPIs(filtered)
    
    set({
      filteredBrands: filtered,
      kpis
    })
  },

  loadFiltersFromURL: () => {
    if (hasURLFilters()) {
      const urlParams = new URLSearchParams(window.location.search)
      const currentFilters = get().filters
      const decodedFilters = decodeFiltersFromURL(urlParams, currentFilters)
      const validatedFilters = validateFiltersFromURL(decodedFilters)
      
      set({ filters: validatedFilters })
      get().applyFilters()
    }
  },

  updateURL: () => {
    const { filters } = get()
    updateURLWithFilters(filters)
  },
  
  // CRUD Operations
  createBrand: async (brand: BrandCreateInput): Promise<BrandOperationResult> => {
    set({ isCreating: true, operationError: null })
    
    try {
      const brandService = getBrandService()
      const result = await brandService.createBrand(brand)
      
      if (!result.success) {
        set({ isCreating: false, operationError: result.error })
        return { success: false, error: result.error }
      }
      
      // Add to allBrands and reapply filters
      const newBrand = result.data
      set(state => ({
        allBrands: [...state.allBrands, newBrand],
        isCreating: false
      }))
      
      get().applyFilters()
      
      return { success: true, data: newBrand }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create brand'
      set({ isCreating: false, operationError: errorMessage })
      return { success: false, error: errorMessage }
    }
  },
  
  updateBrand: async (brandName: string, updates: BrandUpdateInput): Promise<BrandOperationResult> => {
    set({ isUpdating: true, operationError: null })
    
    // Optimistic update
    const currentBrand = get().allBrands.find(b => b.brand === brandName)
    if (currentBrand) {
      const optimisticBrand: ParsedBrand = { 
        ...currentBrand, 
        ...updates as Partial<ParsedBrand>, 
        last_verified: new Date().toISOString() 
      }
      set(state => ({
        pendingChanges: [...state.pendingChanges.filter(b => b.brand !== brandName), optimisticBrand]
      }))
      get().applyFilters()
    }
    
    try {
      const brandService = getBrandService()
      const result = await brandService.updateBrand(brandName, updates)
      
      if (!result.success) {
        // Revert optimistic update
        get().revertOptimisticUpdate(brandName)
        set({ isUpdating: false, operationError: result.error })
        return { success: false, error: result.error }
      }
      
      // Commit the update
      const updatedBrand = result.data
      set(state => ({
        allBrands: state.allBrands.map(b => b.brand === brandName ? updatedBrand : b),
        pendingChanges: state.pendingChanges.filter(b => b.brand !== brandName),
        isUpdating: false
      }))
      
      get().applyFilters()
      
      return { success: true, data: updatedBrand }
    } catch (error) {
      // Revert optimistic update
      get().revertOptimisticUpdate(brandName)
      const errorMessage = error instanceof Error ? error.message : 'Failed to update brand'
      set({ isUpdating: false, operationError: errorMessage })
      return { success: false, error: errorMessage }
    }
  },
  
  deleteBrand: async (brandName: string): Promise<BrandOperationResult> => {
    set({ isDeleting: true, operationError: null })
    
    // Optimistic removal
    const brandToDelete = get().allBrands.find(b => b.brand === brandName)
    if (brandToDelete) {
      set(state => ({
        allBrands: state.allBrands.filter(b => b.brand !== brandName)
      }))
      get().applyFilters()
    }
    
    try {
      const brandService = getBrandService()
      const result = await brandService.deleteBrand(brandName)
      
      if (!result.success) {
        // Revert optimistic removal
        if (brandToDelete) {
          set(state => ({
            allBrands: [...state.allBrands, brandToDelete]
          }))
          get().applyFilters()
        }
        set({ isDeleting: false, operationError: result.error })
        return { success: false, error: result.error }
      }
      
      set({ isDeleting: false })
      return { success: true }
    } catch (error) {
      // Revert optimistic removal
      if (brandToDelete) {
        set(state => ({
          allBrands: [...state.allBrands, brandToDelete]
        }))
        get().applyFilters()
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete brand'
      set({ isDeleting: false, operationError: errorMessage })
      return { success: false, error: errorMessage }
    }
  },
  
  bulkCreateBrands: async (brands: BrandCreateInput[]): Promise<BrandOperationResult> => {
    set({ isCreating: true, operationError: null })
    
    try {
      const brandService = getBrandService()
      const result = await brandService.createBulk(brands)
      
      if (!result.success) {
        set({ isCreating: false, operationError: result.error })
        return { success: false, error: result.error }
      }
      
      // Add all new brands
      set(state => ({
        allBrands: [...state.allBrands, ...result.data],
        isCreating: false
      }))
      
      get().applyFilters()
      
      return { success: true, data: result.data }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create brands'
      set({ isCreating: false, operationError: errorMessage })
      return { success: false, error: errorMessage }
    }
  },
  
  bulkUpdateBrands: async (updates: { brandName: string; updates: BrandUpdateInput }[]): Promise<BrandOperationResult> => {
    set({ isUpdating: true, operationError: null })
    
    try {
      const brandService = getBrandService()
      const result = await brandService.updateBulk(updates)
      
      if (!result.success) {
        set({ isUpdating: false, operationError: result.error })
        return { success: false, error: result.error }
      }
      
      // Update all brands
      const updatedBrandsMap = new Map(result.data.map(b => [b.brand, b]))
      set(state => ({
        allBrands: state.allBrands.map(b => updatedBrandsMap.get(b.brand) || b),
        isUpdating: false
      }))
      
      get().applyFilters()
      
      return { success: true, data: result.data }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update brands'
      set({ isUpdating: false, operationError: errorMessage })
      return { success: false, error: errorMessage }
    }
  },
  
  bulkDeleteBrands: async (brandNames: string[]): Promise<BrandOperationResult> => {
    set({ isDeleting: true, operationError: null })
    
    // Store brands for potential rollback
    const brandsToDelete = get().allBrands.filter(b => brandNames.includes(b.brand))
    
    // Optimistic removal
    set(state => ({
      allBrands: state.allBrands.filter(b => !brandNames.includes(b.brand))
    }))
    get().applyFilters()
    
    try {
      const brandService = getBrandService()
      const result = await brandService.deleteBulk(brandNames)
      
      if (!result.success) {
        // Revert optimistic removal
        set(state => ({
          allBrands: [...state.allBrands, ...brandsToDelete]
        }))
        get().applyFilters()
        set({ isDeleting: false, operationError: result.error })
        return { success: false, error: result.error }
      }
      
      set({ isDeleting: false })
      return { success: true }
    } catch (error) {
      // Revert optimistic removal
      set(state => ({
        allBrands: [...state.allBrands, ...brandsToDelete]
      }))
      get().applyFilters()
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete brands'
      set({ isDeleting: false, operationError: errorMessage })
      return { success: false, error: errorMessage }
    }
  },
  
  // Utility Actions
  getBrandByName: (brandName: string): ParsedBrand | null => {
    const { allBrands, pendingChanges } = get()
    
    // Check pending changes first
    const pendingBrand = pendingChanges.find(b => b.brand === brandName)
    if (pendingBrand) return pendingBrand
    
    return allBrands.find(b => b.brand === brandName) || null
  },
  
  searchBrands: (query: string): ParsedBrand[] => {
    const { allBrands } = get()
    const searchLower = query.toLowerCase()
    
    return allBrands.filter(brand => 
      brand.brand.toLowerCase().includes(searchLower) ||
      brand.consumer_summary.toLowerCase().includes(searchLower) ||
      brand.primary_products.toLowerCase().includes(searchLower) ||
      brand.clean_categories.some(cat => cat.toLowerCase().includes(searchLower))
    )
  },
  
  clearOperationError: () => {
    set({ operationError: null })
  },
  
  revertOptimisticUpdate: (brandName: string) => {
    set(state => ({
      pendingChanges: state.pendingChanges.filter(b => b.brand !== brandName)
    }))
    get().applyFilters()
  },
  
  commitOptimisticUpdates: () => {
    const { pendingChanges } = get()
    
    set(state => ({
      allBrands: state.allBrands.map(brand => {
        const pendingBrand = pendingChanges.find(p => p.brand === brand.brand)
        return pendingBrand || brand
      }),
      pendingChanges: []
    }))
  }
}))